import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RotateCcw, ArrowRight, FileText, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const SCENARIO_LABEL = {
  phase2: "I-589 · Centro de detención",
  phase2_onboarding: "Cómo llamar desde el centro",
  detained: "Detenido por ICE",
  door: "ICE en la puerta",
  street: "ICE en la calle",
  general: "Información general",
};

/**
 * Surfaces the user's most recent active phase2 conversation so they can resume
 * after a 20-min call cut-off. The AI keeps full memory of the prior messages.
 */
export default function ResumeCallCard() {
  const navigate = useNavigate();
  const [last, setLast] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/conversations/last-active?scenario=phase2")
      .then((r) => setLast(r.data.conversation))
      .catch(() => setLast(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !last || (last.messages_count || 0) === 0) return null;

  const expired = last.time_status?.expired;
  const remainingSecs = Number(last.time_status?.remaining_seconds || 0);
  const remainingMin = Math.floor(remainingSecs / 60);

  const resume = () => {
    navigate(`/scenario/phase2`, {
      state: { conversationId: last.id },
    });
  };

  return (
    <section
      data-testid="dashboard-resume-call"
      className="rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-5 mb-5"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center">
          <RotateCcw className="w-5 h-5 text-white" strokeWidth={2.5} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-widest font-black text-emerald-900">
            Reanudar tu última llamada
          </p>
          <p className="text-xs text-emerald-800">
            La IA recuerda todo lo que ya hablamos
          </p>
        </div>
      </div>

      <div className="bg-white border border-emerald-200 rounded-xl p-3 mb-3">
        <div className="flex items-start gap-2 mb-2">
          <FileText className="w-4 h-4 text-emerald-700 mt-0.5 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-xs font-black uppercase tracking-wider text-emerald-800">
              {SCENARIO_LABEL[last.scenario] || last.scenario}
            </p>
            <p className="text-[10px] text-zinc-500">
              {last.messages_count} mensajes · actualizada {new Date(last.updated_at).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" })}
            </p>
          </div>
        </div>
        {last.closing_summary && (
          <div
            data-testid="resume-card-closing-summary"
            className="text-xs text-zinc-800 leading-snug bg-amber-50 border-l-4 border-amber-500 pl-3 py-2 rounded-r-md"
          >
            <p className="font-black text-[10px] uppercase tracking-wider text-amber-800 mb-1">
              Resumen de cierre
            </p>
            <p className="whitespace-pre-wrap">{last.closing_summary}</p>
          </div>
        )}
        {!last.closing_summary && last.last_assistant_preview && (
          <p className="text-xs text-zinc-700 leading-snug italic line-clamp-3 bg-zinc-50 border-l-2 border-emerald-400 pl-2 py-1">
            “{last.last_assistant_preview}…”
          </p>
        )}
      </div>

      {expired ? (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
          <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-900 leading-snug">
            <span className="font-bold">Tu tiempo terminó en esta llamada.</span> Compra +20 minutos por $8 para retomar exactamente donde se quedó.
          </p>
        </div>
      ) : (
        <p className="text-xs text-zinc-700 leading-snug mb-3">
          Te quedan aproximadamente <span className="font-bold">{remainingMin} minutos</span> en esta llamada.
        </p>
      )}

      <Button
        data-testid="dashboard-resume-call-cta"
        onClick={expired ? () => navigate("/precios?reason=time_expired&plan=extra_time") : resume}
        className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-black rounded-xl flex items-center justify-center gap-2"
      >
        {expired ? "Comprar +20 min y reanudar" : "Reanudar mi llamada"}
        <ArrowRight className="w-4 h-4" />
      </Button>
    </section>
  );
}
